import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
} from "@angular/core";
import { Chart, registerables } from "chart.js";
import { format, startOfYear, subDays } from "date-fns";
import { SharedModule } from "../../../../shared/shared.module";
import { Widget } from "../../models/dashboard.model";
import {
  EVOLUTION_VALEUR_DATA,
  EvolutionGranularity,
} from "../../models/data.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { WidgetPeriodFiltersComponent } from "../../../../shared/components/widget-period-filters/widget-period-filters.component";
import { WidgetHelperService } from "../../services/widget-helper.service";
import {
  BackfillRange,
  WidgetDataApiService,
} from "../../services/widget-data-api.service";
import { WidgetDataFacadeService } from "../../services/widget-data-facade.service";
import { SnackService } from "../../../../shared/services/snack-service.service";
import { TranslateUtils } from "../../../../shared/utils/translate.utils";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { EvolutionValeurDialogComponent } from "./evolution-valeur-dialog.component";

// Chart.js modules sont enregistrés globalement par le bootstrap, mais on
// register defensivement ici si jamais ce widget est lazy-loadé en isolation.
Chart.register(...registerables);

/**
 * Widget compact d'évolution de la valeur du stock — sparkline + valeur
 * actuelle + delta % vs début de plage. Le sélecteur de plage (1m / 3m /
 * 6m / exercice / 1a) conditionne :
 * - la fenêtre de la sparkline (et la granularité utilisée)
 * - la plage rejouée par le bouton "Actualiser" qui déclenche un backfill
 *   complet (rejeu des mouvements + coûts historiques) côté back
 *
 * # Particularité
 * Self-fetching (pas via le facade widget standard). L'endpoint a besoin
 * de paramètres (granularity/dimension/dates) que le facade générique ne
 * sait pas porter. Le widget appelle `loadEvolutionValeur()` au mount,
 * lors d'un changement de plage, et après chaque backfill.
 */
@Component({
  selector: "vl-widget-evolution-valeur",
  standalone: true,
  imports: [
    SharedModule,
    WidgetTitleComponent,
    ChipsComponent,
    WidgetPeriodFiltersComponent,
    MatIcon,
    MatTooltip,
  ],
  host: { class: "widget-component" },
  templateUrl: "./widget-evolution-valeur.component.html",
  styleUrl: "./widget-evolution-valeur.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetEvolutionValeurComponent implements OnDestroy {
  widget = input<Widget>();
  canvasRef = viewChild<ElementRef>("spark");

  private api = inject(WidgetDataApiService);
  private snack = inject(SnackService);
  private dialogsService = inject(DialogsService);
  private widgetDataFacade = inject(WidgetDataFacadeService);

  /** Données chargées par le widget. `null` tant qu'aucun fetch n'a réussi. */
  data = signal<EVOLUTION_VALEUR_DATA | null>(null);
  loading = signal<boolean>(true);
  /** True pendant un backfill (peut prendre plusieurs secondes). Désactive
   *  les boutons et fait tourner l'icône sync. */
  refreshing = signal<boolean>(false);

  /** Plage actuellement sélectionnée. Pilote sparkline ET backfill. */
  range = signal<BackfillRange>("1m");

  /**
   * Mapping `BackfillRange` (back, valeurs sans espace) ↔ `timeScale`
   * (composant standard `vl-widget-period-filters`, valeurs avec espace).
   * Les valeurs du standard non couvertes par `BackfillRange` (YTD, 5 a)
   * tombent en fallback raisonnable : YTD ≈ exercice (1er Jan → today),
   * 5 a → 1 a (limite back).
   */
  private static rangeToTimeScale(r: BackfillRange): string {
    switch (r) {
      case "1m":
        return "1 m";
      case "3m":
        return "3 m";
      case "6m":
        return "6 m";
      case "1y":
        return "1 a";
      case "exercice":
        return "EX";
    }
  }

  private static timeScaleToRange(s: string): BackfillRange {
    switch (s) {
      case "1 m":
        return "1m";
      case "3 m":
        return "3m";
      case "6 m":
        return "6m";
      case "1 a":
        return "1y";
      case "EX":
      case "YTD":
        return "exercice";
      case "5 a":
        return "1y";
      default:
        return "1m";
    }
  }

  /** Échelle temporelle exposée au composant standard (read-only via mapping). */
  timeScale = computed<string>(() =>
    WidgetEvolutionValeurComponent.rangeToTimeScale(this.range()),
  );

  /** Setter binder pour two-way avec `vl-widget-period-filters`. */
  setTimeScale(scale: string) {
    this.range.set(WidgetEvolutionValeurComponent.timeScaleToRange(scale));
  }

  chart: Chart | null = null;

  // ─── Affichage agrégé ────────────────────────────────────────────────────

  valeurActuelle = computed(() => {
    const d = this.data();
    if (!d) return "- €";
    return WidgetHelperService.formatNumeric(d.delta.endValue) + " €";
  });

  /** Variation absolue formatée avec signe (ex: "+12,3 k€" / "-8 k€"). */
  deltaAbsolu = computed(() => {
    const d = this.data();
    if (!d) return "";
    const abs = d.delta.absolute;
    const sign = abs >= 0 ? "+" : "−";
    return `${sign}${WidgetHelperService.formatNumeric(Math.abs(abs))} €`;
  });

  /** Variation relative formatée. `null` si non exprimable (start = 0). */
  deltaPercent = computed(() => {
    const d = this.data();
    if (!d || d.delta.percent == null) return null;
    const sign = d.delta.percent >= 0 ? "+" : "";
    return `${sign}${d.delta.percent.toFixed(1)}%`;
  });

  /** Couleur sémantique du delta (positive=vert, negative=rouge, neutre=gris). */
  deltaClass = computed<"positive" | "negative" | "neutral">(() => {
    const d = this.data();
    if (!d || d.delta.absolute === 0) return "neutral";
    return d.delta.absolute > 0 ? "positive" : "negative";
  });

  /** Banner "données disponibles depuis JJ/MM/AAAA" : visible tant que
   *  l'historique demandé excède la profondeur réelle des snapshots. */
  partialHistory = computed<string | null>(() => {
    const d = this.data();
    if (!d || !d.availableSince) return null;
    if (d.availableSince <= d.startDate) return null;
    return d.availableSince;
  });

  /**
   * Indique si la plage demandée n'est pas couverte intégralement par
   * les snapshots (manque de données historiques). On considère partiel :
   * - si `availableSince` est postérieur au `startDate` demandé
   * - OU si la plage d'un mois n'a pas au moins ~70% des points attendus
   *
   * Quand vrai, on propose un CTA "Recalculer" pour déclencher le backfill
   * sans que l'utilisateur ait à cliquer sur l'icône sync (UX plus claire).
   */
  needsBackfill = computed<boolean>(() => {
    const d = this.data();
    if (!d) return false;
    // Pas encore de données → toujours proposer
    if ((d.total?.length ?? 0) === 0) return true;
    // availableSince dépasse startDate → historique incomplet
    if (d.availableSince && d.availableSince > d.startDate) return true;
    return false;
  });

  hasData = computed(() => {
    const points = this.data()?.total ?? [];
    return points.length > 0;
  });

  /** Une courbe à 1 point n'est pas visible (line chart sans segment).
   *  On affiche alors un placeholder textuel + invite à actualiser. */
  singlePoint = computed(() => {
    const points = this.data()?.total ?? [];
    return points.length === 1;
  });

  /** Libellé du delta dynamique selon la plage : "Variation 1M", "Exercice"… */
  deltaLabel = computed(() => {
    const r = this.range();
    if (r === "exercice") return "Variation exercice :";
    return `Variation ${r.toUpperCase()} :`;
  });

  constructor() {
    // Premier chargement automatique
    this.fetch();

    // Reconstruit le graph dès que les données ou le canvas changent
    effect(
      () => {
        const d = this.data();
        if (d?.total?.length && this.canvasRef()) {
          this.buildChart(d.total);
        }
      },
      { allowSignalWrites: true },
    );

    // Refetch dès qu'on change la plage (sans relancer le calcul, juste
    // afficher ce qu'on a en base sur la nouvelle fenêtre)
    effect(
      () => {
        // déclenche sur changement de range
        this.range();
        this.fetch();
      },
      { allowSignalWrites: true },
    );

    // Self-fetching widget : il ne passe pas par le facade standard, donc
    // le bouton "Actualiser" du dashboard (qui vide le cache + incrémente
    // refreshTick) ne le rafraîchissait pas. On s'abonne explicitement
    // pour rejouer `fetch()` à chaque tick.
    let firstTick = true;
    effect(
      () => {
        const tick = this.widgetDataFacade.refreshTick();
        // skip le tick initial (déjà couvert par le `fetch()` du constructor)
        if (firstTick) {
          firstTick = false;
          return;
        }
        void tick;
        this.fetch();
      },
      { allowSignalWrites: true },
    );
  }

  // ─── Chargement ──────────────────────────────────────────────────────────

  /**
   * Convertit une plage logique en bornes ISO + granularité de lecture.
   * Pour les longues plages on agrège côté back (semaine/mois) afin de
   * garder la sparkline lisible (pas 365 points).
   */
  private resolveRange(range: BackfillRange): {
    startDate: string;
    endDate: string;
    granularity: EvolutionGranularity;
  } {
    const today = new Date();
    const endDate = format(today, "yyyy-MM-dd");
    let startDate: string;
    let granularity: EvolutionGranularity = "day";

    switch (range) {
      case "1m":
        startDate = format(subDays(today, 30), "yyyy-MM-dd");
        granularity = "day";
        break;
      case "3m":
        startDate = format(subDays(today, 90), "yyyy-MM-dd");
        granularity = "day";
        break;
      case "6m":
        startDate = format(subDays(today, 180), "yyyy-MM-dd");
        granularity = "week";
        break;
      case "1y":
        startDate = format(subDays(today, 365), "yyyy-MM-dd");
        granularity = "week";
        break;
      case "exercice":
        startDate = format(startOfYear(today), "yyyy-MM-dd");
        granularity = "week";
        break;
    }
    return { startDate, endDate, granularity };
  }

  fetch() {
    this.loading.set(true);
    const { startDate, endDate, granularity } = this.resolveRange(this.range());
    this.api
      .loadEvolutionValeur(granularity, "total", startDate, endDate)
      .subscribe({
        next: (data) => {
          this.data.set(data);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  /**
   * Lance le backfill côté back sur la plage actuellement sélectionnée
   * (rejeu des mouvements + coûts historiques). Idempotent. Une fois
   * terminé, refetch automatique pour afficher la nouvelle courbe.
   */
  refreshNow() {
    if (this.refreshing()) return;
    this.refreshing.set(true);
    const range = this.range();
    this.api.runBackfill(range).subscribe({
      next: (res) => {
        this.snack.success(
          TranslateUtils.translate("dashboard_ui.evolution_valeur_backfilled", {
            days: res.daysProcessed,
            count: res.rowsInserted,
            mouvements: res.mouvementsProcessed,
          }),
        );
        this.fetch();
        this.refreshing.set(false);
      },
      error: () => this.refreshing.set(false),
    });
  }

  // ─── Chart.js ────────────────────────────────────────────────────────────

  private buildChart(points: EVOLUTION_VALEUR_DATA["total"]) {
    const canvas = this.canvasRef()?.nativeElement;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (this.chart) this.chart.destroy();

    const positive = this.deltaClass() !== "negative";
    const lineColor = positive
      ? "rgba(46, 161, 122, 1)" // valid
      : "rgba(231, 76, 60, 1)"; // error
    const fillColor = positive
      ? "rgba(46, 161, 122, 0.15)"
      : "rgba(231, 76, 60, 0.15)";

    this.chart = new Chart(ctx, {
      type: "line",
      data: {
        labels: points.map((p) => p.date),
        datasets: [
          {
            data: points.map((p) => p.total),
            borderColor: lineColor,
            backgroundColor: fillColor,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.25,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => items[0]?.label ?? "",
              label: (item) =>
                `${WidgetHelperService.formatNumeric(item.parsed.y as number)} €`,
            },
          },
        },
        scales: {
          x: {
            // Quelques labels seulement pour rester lisible sur le widget
            // compact (largeur ~400 px). 4 ticks max suffisent à donner
            // un sens temporel sans encombrer.
            ticks: {
              autoSkip: true,
              maxTicksLimit: 4,
              font: { size: 9 },
              color: "rgba(0,0,0,0.4)",
            },
            grid: { display: false },
            border: { display: false },
          },
          y: {
            beginAtZero: false,
            // Échelle Y compacte — 3 ticks formatés (k€/M€) pour donner
            // l'ordre de grandeur sans charger visuellement.
            ticks: {
              maxTicksLimit: 3,
              font: { size: 9 },
              color: "rgba(0,0,0,0.4)",
              callback: (v) =>
                WidgetHelperService.formatNumeric(v as number) + " €",
            },
            grid: { color: "rgba(0,0,0,0.05)" },
            border: { display: false },
          },
        },
        interaction: { mode: "index", intersect: false },
      },
    });
  }

  // ─── Voir tout (dialog) ──────────────────────────────────────────────────

  /**
   * Ouvre le dialog plein-écran avec sélecteurs de plage / granularité /
   * dimension. Initialise le dialog avec la plage actuellement choisie sur
   * le widget compact pour continuité visuelle.
   */
  openFullList() {
    // Largeur 96vw : laisse `.justup-dialog-large` (min-width 82vw / 1280px)
    // tenir confortablement sans déclencher de scroll horizontal sur le
    // host MatDialog. Hauteur fixée à 92vh pour que le contenu interne
    // puisse scroller (drill-down sous-zones d'entrepôt peut générer
    // beaucoup de lignes) — `auto` laisserait le dialog déborder du
    // viewport sans scroll possible.
    this.dialogsService.open(
      EvolutionValeurDialogComponent,
      "96vw",
      "92vh",
      { initialRange: this.range() },
    );
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
